import React, { createContext, type ReactNode, useContext, useMemo, useReducer } from 'react';

import { StateContext } from './state-context';
import type { EntryPointPanel, Panel, PanelAction, PanelSystemState } from './types';

/**
 * Context for accessing the panel manager functions.
 * Provides a stable reference that doesn't cause re-renders on state changes.
 * @internal
 */
export const ManagerContext: React.Context<PanelManager | null> =
	createContext<PanelManager | null>(null);

/**
 * Panel manager providing action methods for panel operations
 * @internal
 */
export type PanelManager = {
	openPanel: (
		instanceId: string,
		entryPoint: any,
		params?: any,
		entryPointReference?: any,
		shouldReplace?: boolean,
	) => void;
	closePanel: (instanceId: string) => void;
	replacePanelEntryPoint: (
		instanceId: string,
		entryPoint: any,
		params?: any,
		entryPointReference?: any,
	) => void;
	replacePanel: (instanceId: string, panelContent: ReactNode, params?: any) => void;
	saveEntryPointReference: (instanceId: string, entryPointReference: any) => void;
};

/**
 * Props for the PanelProvider component
 */
export type PanelProviderProps = {
	/**
	 * Child components that will have access to the panel manager
	 */
	children: ReactNode;
	/**
	 * Optional initial state for the panel system
	 */
	initialState?: Partial<PanelSystemState>;
};

/**
 * Pure reducer function handling panel state transitions.
 * Manages PANEL_OPEN and PANEL_CLOSE actions.
 *
 * Note: Panel resizing (width) is handled locally in PanelContainer as UI state,
 * not global application state.
 *
 * TODO: For SSR support, panel widths should be persisted to localStorage
 * so that user preferences are restored across page reloads.
 */
function panelReducer(state: PanelSystemState, action: PanelAction): PanelSystemState {
	switch (action.type) {
		case 'PANEL_OPEN': {
			const { instanceId, panelContent, params, shouldReplace } = action.payload;
			const newPanel: Panel = {
				instanceId,
				panelContent,
				params: params || {},
			};

			if (shouldReplace) {
				return {
					...state,
					activePanels: [newPanel],
				};
			}
			return {
				...state,
				activePanels: [...state.activePanels, newPanel],
			};
		}

		case 'PANEL_OPEN_ENTRYPOINT': {
			const { instanceId, entryPoint, params, entryPointReference, shouldReplace } = action.payload;
			const newPanel: EntryPointPanel = {
				instanceId,
				entryPoint,
				params: params || {},
				entryPointReference,
			};

			if (shouldReplace) {
				return {
					...state,
					activePanels: [newPanel],
				};
			}
			return {
				...state,
				activePanels: [...state.activePanels, newPanel],
			};
		}

		case 'PANEL_CLOSE': {
			const { instanceId } = action.payload;
			return {
				...state,
				activePanels: state.activePanels.filter((p) => p.instanceId !== instanceId),
			};
		}

		case 'PANEL_SAVE_ENTRYPOINT': {
			const { instanceId, entryPointReference } = action.payload;
			// If panel is already saved, do nothing
			if (state.preloadedPanels.find((panel) => panel.instanceId === instanceId)) {
				return state;
			} else {
				const preloadedPanel = {
					instanceId,
					entryPointReference,
				};
				return {
					...state,
					preloadedPanels: [...state.preloadedPanels, preloadedPanel],
				};
			}
		}

		default:
			return state;
	}
}

/**
 * PanelProvider component that wraps your application to provide panel management context.
 *
 * Uses split contexts for optimal performance:
 * - ManagerContext: Provides stable panel manager functions (never re-renders consumers)
 * - StateContext: Provides panel state (re-renders consumers when state changes)
 *
 * This design prevents unnecessary re-renders of components that only use the manager.
 *
 * @example
 * ```tsx
 * import { PanelProvider, usePanelManager } from '@atlassian/panel-system';
 *
 * export function App() {
 *   return (
 *     <PanelProvider>
 *       <YourComponent />
 *     </PanelProvider>
 *   );
 * }
 *
 * function YourComponent() {
 *   const { state, manager } = usePanelManager();
 *   return (
 *     <button onClick={() => manager.openPanel('id-1', entryPoint)}>
 *       Open Panel
 *     </button>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function PanelProvider({ children, initialState }: PanelProviderProps): React.JSX.Element {
	const [state, dispatch] = useReducer(panelReducer, {
		activePanels: [],
		preloadedPanels: [],
		...initialState,
	});

	// Create stable manager object with memoization
	// This object reference never changes, so ManagerContext consumers never re-render
	const manager = useMemo(() => {
		return {
			/**
			 * Opens a panel with the given instance ID, entry point, and params.
			 * By default, this replaces any existing panels (single panel mode).
			 *
			 * **Note:** This is a low-level API. For most use cases, prefer using the `Trigger` component,
			 * which handles entry point loading and ID generation automatically.
			 *
			 * **ID Generation:** The instanceId must be provided. For best practices, use the `Trigger`
			 * component's instanceId (either provided or auto-generated) rather than generating IDs
			 * at the manager level. This keeps ID management co-located with the trigger.
			 *
			 * @param instanceId - Unique identifier for this panel instance. Should come from Trigger when possible.
			 * @param entryPoint - Entry point definition (not the loaded reference)
			 * @param params - Optional params to pass to the panel
			 * @param entryPointReference - Optional preloaded entry point reference (typically provided by Trigger)
			 */
			openPanel: (
				instanceId: string,
				entryPoint: any,
				params?: any,
				entryPointReference?: any,
				shouldReplace: boolean = true,
			) => {
				dispatch({
					type: 'PANEL_OPEN_ENTRYPOINT',
					payload: {
						instanceId,
						entryPoint,
						params,
						entryPointReference,
						shouldReplace,
					},
				});
			},

			closePanel: (instanceId: string) => {
				dispatch({
					type: 'PANEL_CLOSE',
					payload: {
						instanceId,
					},
				});
			},

			/**
			 * Replaces the current panel(s) with a new panel via entrypoints.
			 * This is a convenience method that closes all existing panels and opens a new one.
			 *
			 * @param instanceId - Unique identifier for this panel instance
			 * @param entryPoint - Entry point definition (not the loaded reference)
			 * @param params - Optional params to pass to the panel
			 * @param entryPointReference - Optional preloaded entry point reference
			 */
			replacePanelEntryPoint: (
				instanceId: string,
				entryPoint: any,
				params?: any,
				entryPointReference?: any,
			) => {
				dispatch({
					type: 'PANEL_OPEN_ENTRYPOINT',
					payload: {
						instanceId,
						entryPoint,
						params,
						entryPointReference,
						shouldReplace: true,
					},
				});
			},
			/**
			 * Replaces the current panel(s) with a new panel by providing panel content.
			 * This is a convenience method that closes all existing panels and opens a new one.
			 *
			 * @param instanceId - Unique identifier for this panel instance
			 * @param panelContent - Panel content (ReactNode) to render inside the panel
			 * @param params - Optional params to pass to the panel
			 */
			replacePanel: (instanceId: string, panelContent?: ReactNode, params?: any) => {
				dispatch({
					type: 'PANEL_OPEN',
					payload: {
						instanceId,
						params,
						shouldReplace: true,
						panelContent,
					},
				});
			},
			/**
			 * Saves a preloaded EntryPoint reference for a given panel instance.
			 * Useful for mixing Trigger and programmatic panel opening.
			 *
			 * @param instanceId - Unique identifier for this panel instance
			 * @param entryPointReference - Preloaded EntryPoint reference
			 */
			saveEntryPointReference: (instanceId: string, entryPointReference: any) => {
				dispatch({
					type: 'PANEL_SAVE_ENTRYPOINT',
					payload: {
						instanceId,
						entryPointReference,
					},
				});
			},
		};
	}, []);

	return (
		<ManagerContext.Provider value={manager}>
			<StateContext.Provider value={state}>{children}</StateContext.Provider>
		</ManagerContext.Provider>
	);
}

/**
 * Hook to access the panel manager actions from context.
 * Must be called within a PanelProvider.
 *
 * The manager object is stable and memoized, so using this hook alone
 * won't cause re-renders when state changes.
 *
 * @throws {Error} If called outside of a PanelProvider
 * @internal
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function usePanelActionsInternal(): PanelManager {
	const manager = useContext(ManagerContext);

	if (!manager) {
		throw new Error(
			'usePanelActionsInternal must be used within a <PanelProvider>. ' +
				'Wrap your app with: <PanelProvider><App /></PanelProvider>',
		);
	}

	return manager;
}
